/**
 * Labstep
 *
 * @module grid/NewIndex/coldefs/metadata/molecule/pubchem
 * @desc Column Definition for metadata molecule pubchem fields
 */

import type {
  EditableCallbackParams,
  ValueGetterParams,
  ValueSetterParams,
} from 'ag-grid-community';
import { ICONS } from 'labstep-web/constants/icons';
import type { CellRendererParams } from 'labstep-web/core/DataGrid/types';
import {
  createOrUpdateValue,
  setValue,
} from 'labstep-web/core/DataGrid/utils';
import { TextPlaceholder } from 'labstep-web/core/Text/Placeholder';
import type { colDefPubchemFieldType } from 'labstep-web/grid/Index/coldefs/Molecule/pubchem/types';
import { getMetadata } from 'labstep-web/grid/NewIndex/coldefs';
import type { NewIndexColDef } from 'labstep-web/grid/NewIndex/coldefs/types';
import { GridNewIndexColumnHeader } from 'labstep-web/grid/NewIndex/components/ColumnHeader';
import { GridNewIndexService } from 'labstep-web/grid/NewIndex/services/grid-new-index.service';
import { GridColumnDefinitionService } from 'labstep-web/grid/services/grid-column-definition.service';
import type { Entity } from 'labstep-web/models/entity.model';
import { Metadata } from 'labstep-web/models/metadata';
import type { MetadataType } from 'labstep-web/models/metadata/types';
import { MetadataThread } from 'labstep-web/models/metadata-thread.model';
import { PubchemService } from 'labstep-web/services/pubchem.service';
import { isValid } from 'labstep-web/services/validation';

export const getMetadataMoleculePubchemColDef = (
  parentEntityClass: string,
  type: MetadataType,
  metadataLabel: string,
  fieldName: string,
  field: colDefPubchemFieldType,
  entityName?: string,
): NewIndexColDef => {
  return {
    colId: `metadata:${parentEntityClass}:${type}:${metadataLabel}:${field.name}`,
    sortable: true,
    headerName: fieldName,
    headerComponent: GridNewIndexColumnHeader,
    headerComponentParams: {
      icon: ICONS.metadata.type.molecule,
      postFilterSettings: {
        path:
          parentEntityClass !== entityName
            ? `${parentEntityClass}.metadatas`
            : 'metadatas',
      },
    },
    editable: (params: EditableCallbackParams) => {
      const entity = params.node.data;
      if (
        parentEntityClass === 'purchase_order' &&
        !entity.purchase_order
      ) {
        return false;
      }
      return GridNewIndexService.getEditable(params);
    },
    valueGetter: (
      params: ValueGetterParams,
    ): string | number | null => {
      if (!params.node) {
        return null;
      }
      const metadata = getMetadata(
        parentEntityClass,
        params.node.data,
        metadataLabel,
        type,
      );

      if (!metadata || !metadata.molecule) {
        return '';
      }
      return PubchemService.getPubchemFieldValue(
        metadata.molecule,
        field,
      );
    },
    valueSetter: (params: ValueSetterParams): boolean => {
      if (!params.node) {
        return false;
      }
      const metadata = getMetadata(
        parentEntityClass,
        params.node.data,
        metadataLabel,
        type,
      );

      if (!metadata || !metadata.molecule) {
        return false;
      }
      const molecule = metadata.molecule;
      setValue(
        'pubchem',
        PubchemService.getPubchemBody(
          molecule,
          field,
          params.newValue,
        ),
        {
          entityName: molecule.entityName,
          id: molecule.idAttr,
        },
        (value) => ({
          value,
          error: !isValid(params.newValue, field.validation),
        }),
      );
      return true;
    },
    cellRenderer: (params: CellRendererParams<Entity>) => {
      if (!params.node) {
        return null;
      }

      const entity = params.node.data;
      if (
        parentEntityClass === 'purchase_order' &&
        !entity.purchase_order
      ) {
        return null;
      }

      const metadata = getMetadata(
        parentEntityClass,
        params.node.data,
        metadataLabel,
        type,
      );

      const metadataThreadId =
        parentEntityClass !== params.node.data.entityName
          ? params.node.data[parentEntityClass].metadata_thread.idAttr
          : params.node.data.metadata_thread.idAttr;

      if (!metadata) {
        if (
          parentEntityClass !== params.node.data.entityName &&
          !params.node.data[parentEntityClass]
        ) {
          return false;
        }
        const props = {
          parentName: MetadataThread.entityName,
          parentId: metadataThreadId,
        };

        return (
          <TextPlaceholder
            onClick={() => {
              createOrUpdateValue({
                entityName: Metadata.entityName,
                body: {
                  type,
                  label: metadataLabel,
                  metadata_thread_id: metadataThreadId,
                  molecule: { name: 'Untitled structure' },
                },
                createProps: props,
                options:
                  GridColumnDefinitionService.getOptions(params),
              });
              return true;
            }}
          >
            Create structure
          </TextPlaceholder>
        );
      }
      return PubchemService.getPubchemFieldValue(
        metadata.molecule,
        field,
      );
    },
  };
};
